'use client'

import { getCookie, setCookie } from 'cookies-next'
import { AffiliateData, OrderData, TrafficSourceData } from './types'

const COOKIE_NAME = 'affiliate_content_new'
const COOKIE_EXPIRY_DAYS = 90

export class AffiliateTracker {
  private static instance: AffiliateTracker
  private affiliateData: AffiliateData | null = null

  private constructor() {
    // console.log('AffiliateTracker constructor')
  }

  static getInstance(): AffiliateTracker {
    if (!AffiliateTracker.instance) {
      AffiliateTracker.instance = new AffiliateTracker()
    }
    return AffiliateTracker.instance
  }

  private loadFromCookie(): AffiliateData | null {
    const cookie = getCookie(COOKIE_NAME)
    // console.log('Loading from cookie:', cookie)

    if (!cookie) return null

    try {
      const data = JSON.parse(cookie as string)
      if (data.source) {
        // console.log('Loaded affiliate data from cookie:', data)
        return data
      }
    } catch {
      // console.log('Invalid cookie data, clearing cookie')
      this.clearCookie()
    }
    return null
  }

  private saveToCookie(data: AffiliateData): void {
    // console.log('Saving to cookie:', data)
    setCookie(COOKIE_NAME, JSON.stringify(data), {
      maxAge: COOKIE_EXPIRY_DAYS * 24 * 60 * 60,
      sameSite: 'lax',
      path: '/',
    })
    this.affiliateData = data
  }

  private clearCookie(): void {
    // console.log('Clearing cookie')
    setCookie(COOKIE_NAME, '', { maxAge: 0, path: '/' })
    this.affiliateData = null
  }

  private getTrafficSourceData(
    searchParams: URLSearchParams
  ): TrafficSourceData {
    const now = Math.floor(Date.now() / 1000)

    // Get direct UTM params from URL
    const utmSource = searchParams.get('utm_source')
    const utmMedium = searchParams.get('utm_medium')
    const utmCampaign = searchParams.get('utm_campaign')
    const utmTerm = searchParams.get('utm_term')
    const utmContent = searchParams.get('utm_content')
    const gclid = searchParams.get('gclid')
    const trafficId =
      searchParams.get('traffic_id') || searchParams.get('click_id')
    const trafficChannel = searchParams.get('traffic_channel')

    return {
      time_stamp: now,
      source: utmSource || 'organic',
      traffic_id: trafficId || '',
      traffic_channel: trafficChannel || '',
      utm_medium: utmMedium || '',
      utm_campaign: utmCampaign || '',
      utm_term: utmTerm || '',
      utm_content: utmContent || '',
      gclid: gclid || '',
      url: typeof window !== 'undefined' ? window.location.href : '',
      http_referer: typeof document !== 'undefined' ? document.referrer : '',
      http_user_agent:
        typeof window !== 'undefined' ? window.navigator.userAgent : '',
    }
  }

  init(searchParams?: URLSearchParams): void {
    if (!searchParams) {
      // console.log('No search params provided')
      return
    }

    // Get UTM parameters
    const utmSource = searchParams.get('utm_source')
    const utmMedium = searchParams.get('utm_medium')
    const utmCampaign = searchParams.get('utm_campaign')

    // console.log('URL Parameters:', {
    //   utm_source: utmSource,
    //   utm_medium: utmMedium,
    //   utm_campaign: utmCampaign,
    // })

    // Load existing data from cookie
    const existingData = this.loadFromCookie()
    // console.log('Existing cookie data:', existingData)

    // If we have UTM parameters, always create new tracking data
    if (utmSource || utmMedium || utmCampaign) {
      // console.log('Found UTM parameters, creating new tracking data')
      const trafficData = this.getTrafficSourceData(searchParams)
      const affiliateData: AffiliateData = {
        ...trafficData,
        aff_channel: trafficData.source,
        aff_source: trafficData.source,
        click_id: trafficData.traffic_id,
      }
      this.saveToCookie(affiliateData)
    }
    // If no UTM parameters but we have existing data, keep it
    else if (existingData) {
      // console.log('No UTM parameters, keeping existing data')
      this.affiliateData = existingData
    }
    // If no UTM parameters and no existing data, save as organic
    else {
      // console.log('No UTM parameters and no existing data, saving as organic')
      const trafficData = this.getTrafficSourceData(searchParams)
      const affiliateData: AffiliateData = {
        ...trafficData,
        aff_channel: 'organic',
        aff_source: 'organic',
      }
      this.saveToCookie(affiliateData)
    }
  }

  getClickId(): string {
    return this.affiliateData?.traffic_id || ''
  }

  clearClick(): void {
    this.clearCookie()
  }

  getTrackingData(): AffiliateData | null {
    // Always try to load from cookie first
    if (!this.affiliateData) {
      this.affiliateData = this.loadFromCookie()
    }
    return this.affiliateData
  }

  async sendPostBack(orderData: OrderData): Promise<void> {
    if (!this.affiliateData) return

    try {
      const response = await fetch('/api/affiliate/postback', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          affiliate: this.affiliateData,
          order: orderData,
        }),
      })

      if (!response.ok) {
        throw new Error('Failed to send postback')
      }
    } catch (error) {
      console.error('Error sending affiliate postback:', error)
    }
  }
}
